/**
 * UnoGame — Main container orchestrating game flow, AI turns, UNO calls, state.
 * Wrapped in ErrorBoundary componentName="UNO".
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { UniversalGameLayout, UniversalGameLayoutConfig } from '../../components/UniversalGameLayout';
import { useAuthentication } from '../../hooks/useAuthentication';
import { useNotifications } from '../../hooks/useNotifications';
import { NotificationType } from '../../services/NotificationService';
import { GameSessionManager } from '../../services/GameSessionManager';
import { UnoEngine } from '../UnoEngine';
import { UnoAI } from '../UnoAI';
import { UnoBoard } from './UnoBoard';
import UniversalGameModeSelector, { GameModeConfig, GameModeSelection } from '../../components/UniversalGameModeSelector';
import {
  UnoGameProps, UnoGameSettings, UnoGameConfig, UnoCard, UnoColor,
  VisibleUnoGameState,
} from '../types';
import './UnoGame.css';

type GameView = 'mode-select' | 'playing' | 'game-over';

const UnoGameInner: React.FC<UnoGameProps> = ({ onExit }) => {
  const { isAuthenticated } = useAuthentication();
  const { addNotification } = useNotifications();

  const [view, setView] = useState<GameView>('mode-select');
  const [settings, setSettings] = useState<UnoGameSettings | null>(null);
  const [visibleState, setVisibleState] = useState<VisibleUnoGameState | null>(null);
  const [playerHand, setPlayerHand] = useState<UnoCard[]>([]);
  const [drawnCard, setDrawnCard] = useState<UnoCard | null>(null);

  const engineRef = useRef(new UnoEngine());
  const aiRef = useRef<Map<string, UnoAI>>(new Map());
  const sessionIdRef = useRef<string | null>(null);
  const gsm = useRef(GameSessionManager.getInstance());
  const humanPlayerId = useRef('player-human');
  const aiTurnInProgress = useRef(false);

  const refreshState = useCallback(() => {
    const state = engineRef.current.getVisibleState(humanPlayerId.current);
    setVisibleState(state);
    const fullState = engineRef.current.getGameState();
    const humanPlayer = fullState.players.find(p => p.id === humanPlayerId.current);
    if (humanPlayer) {
      setPlayerHand([...humanPlayer.hand]);
    }
    return state;
  }, []);

  // =========================================================================
  // Game Start
  // =========================================================================

  const handleStartGame = useCallback((newSettings: UnoGameSettings) => {
    const engine = engineRef.current;
    const aiCount = newSettings.aiOpponentCount || 1;
    const totalPlayers = aiCount + 1;

    const config: UnoGameConfig = {
      playerCount: totalPlayers,
      players: [
        { id: humanPlayerId.current, name: 'You', isHuman: true },
        ...Array.from({ length: aiCount }, (_, i) => ({
          id: `ai-${i + 1}`,
          name: `Bot ${i + 1}`,
          isHuman: false,
          aiDifficulty: newSettings.aiDifficulty || 'medium' as const,
        })),
      ],
    };

    engine.initializeGame(config);

    // Create AI instances
    aiRef.current.clear();
    config.players.filter(p => !p.isHuman).forEach(p => {
      aiRef.current.set(p.id, new UnoAI(p.aiDifficulty || 'medium'));
    });

    setSettings(newSettings);
    setView('playing');
    setDrawnCard(null);
    sessionIdRef.current = gsm.current.startGameSession('uno');

    refreshState();

    addNotification({
      type: NotificationType.SUCCESS,
      title: 'Game Started',
      message: `UNO - ${newSettings.aiDifficulty || 'medium'} difficulty`,
      duration: 2000,
    });

    // Check if AI goes first
    scheduleAITurnIfNeeded();
  }, [addNotification, refreshState]);

  // =========================================================================
  // AI Turn Logic
  // =========================================================================

  const scheduleAITurnIfNeeded = useCallback(() => {
    const engine = engineRef.current;
    const currentId = engine.getCurrentPlayerId();
    if (!currentId || currentId === humanPlayerId.current) return;
    if (engine.isGameOver()) return;
    if (aiTurnInProgress.current) return;

    const ai = aiRef.current.get(currentId);
    if (!ai) return;

    aiTurnInProgress.current = true;

    ai.getAction(engine, currentId).then(result => {
      if (engine.isGameOver()) {
        aiTurnInProgress.current = false;
        return;
      }

      if (result.action === 'draw') {
        handleAIDraw(currentId, ai);
      } else {
        handleAIPlay(currentId, result.card, result.chosenColor);
      }
    }).catch(() => {
      aiTurnInProgress.current = false;
    });
  }, []);

  const handleAIPlay = useCallback((playerId: string, card: UnoCard, chosenColor?: UnoColor) => {
    const engine = engineRef.current;
    const player = engine.getGameState().players.find(p => p.id === playerId);

    // Call UNO if down to 1 card after playing
    if (player && player.hand.length === 2) {
      engine.callUno(playerId);
      addNotification({
        type: NotificationType.INFO,
        title: `${player.name} calls UNO!`,
        message: '',
        duration: 1500,
      });
    }

    const result = engine.playCard(playerId, card.id, chosenColor);

    if (result.success) {
      const state = refreshState();

      const playerName = engine.getGameState().players.find(p => p.id === playerId)?.name || 'AI';
      const cardDesc = getCardDescription(card, chosenColor);
      addNotification({
        type: NotificationType.INFO,
        title: `${playerName} played ${cardDesc}`,
        message: '',
        duration: 1500,
      });

      if (engine.isGameOver()) {
        handleGameOver();
        aiTurnInProgress.current = false;
        return;
      }

      aiTurnInProgress.current = false;
      // Schedule next AI turn if needed
      setTimeout(() => scheduleAITurnIfNeeded(), 300);
    } else {
      aiTurnInProgress.current = false;
    }
  }, [addNotification, refreshState]);

  const handleAIDraw = useCallback((playerId: string, ai: UnoAI) => {
    const engine = engineRef.current;
    const result = engine.drawCard(playerId);

    if (result.success) {
      if (result.canPlay && result.drawnCard) {
        // AI decides whether to play the drawn card
        if (ai.shouldPlayDrawnCard(result.drawnCard, engine, playerId)) {
          const chosenColor = (result.drawnCard.type === 'wild' || result.drawnCard.type === 'wild-draw-four')
            ? ai.chooseColor(engine.getGameState().players.find(p => p.id === playerId)?.hand || [])
            : undefined;

          const playResult = engine.playDrawnCard(playerId, result.drawnCard.id, chosenColor);
          if (playResult.success) {
            refreshState();
            if (engine.isGameOver()) {
              handleGameOver();
              aiTurnInProgress.current = false;
              return;
            }
          }
        } else {
          engine.passTurn(playerId);
        }
      }

      refreshState();
      aiTurnInProgress.current = false;
      setTimeout(() => scheduleAITurnIfNeeded(), 300);
    } else {
      aiTurnInProgress.current = false;
    }
  }, [refreshState]);

  // =========================================================================
  // Human Player Actions
  // =========================================================================

  const handlePlayCard = useCallback((card: UnoCard, chosenColor?: UnoColor) => {
    const engine = engineRef.current;
    setDrawnCard(null);

    // Auto-call UNO if player has 2 cards (will have 1 after playing)
    const player = engine.getGameState().players.find(p => p.id === humanPlayerId.current);
    if (player && player.hand.length === 2) {
      engine.callUno(humanPlayerId.current);
    }

    const result = engine.playCard(humanPlayerId.current, card.id, chosenColor);

    if (result.success) {
      const state = refreshState();

      if (engine.isGameOver()) {
        handleGameOver();
        return;
      }

      // Schedule AI turns
      setTimeout(() => scheduleAITurnIfNeeded(), 500);
    } else {
      addNotification({
        type: NotificationType.WARNING,
        title: 'Invalid Move',
        message: result.error || 'Cannot play that card',
        duration: 2000,
      });
    }
  }, [addNotification, refreshState]);

  const handleDrawCard = useCallback(() => {
    const engine = engineRef.current;
    const result = engine.drawCard(humanPlayerId.current);

    if (result.success) {
      if (result.canPlay && result.drawnCard) {
        setDrawnCard(result.drawnCard);
        addNotification({
          type: NotificationType.INFO,
          title: 'Card Drawn',
          message: 'You can play the drawn card or pass',
          duration: 2000,
        });
      } else {
        setDrawnCard(null);
        addNotification({
          type: NotificationType.INFO,
          title: result.drawnCard ? 'Card Drawn' : 'Cards Drawn',
          message: "Can't play, turn passed",
          duration: 1500,
        });
      }

      refreshState();

      // If turn was passed (no playable drawn card), schedule AI
      if (!result.canPlay) {
        setTimeout(() => scheduleAITurnIfNeeded(), 500);
      }
    }
  }, [addNotification, refreshState]);

  const handleCallUno = useCallback(() => {
    const engine = engineRef.current;
    const result = engine.callUno(humanPlayerId.current);

    if (result.success) {
      addNotification({
        type: NotificationType.SUCCESS,
        title: 'UNO!',
        message: 'You called UNO!',
        duration: 2000,
      });
      refreshState();
    }
  }, [addNotification, refreshState]);

  // =========================================================================
  // Game Over
  // =========================================================================

  const handleGameOver = useCallback(() => {
    setView('game-over');
    if (sessionIdRef.current) {
      gsm.current.endGameSession(sessionIdRef.current, true);
      sessionIdRef.current = null;
    }
    const winner = engineRef.current.getWinner();
    const isHumanWinner = winner === humanPlayerId.current;
    addNotification({
      type: isHumanWinner ? NotificationType.SUCCESS : NotificationType.INFO,
      title: '🎉 Game Over!',
      message: isHumanWinner ? 'Congratulations! You won!' : 'Better luck next time!',
      duration: 5000,
    });
  }, [addNotification]);

  const handleNewGame = useCallback(() => {
    aiRef.current.clear();
    aiTurnInProgress.current = false;
    setView('mode-select');
    setVisibleState(null);
    setPlayerHand([]);
    setSettings(null);
    setDrawnCard(null);
  }, []);

  const handleExit = useCallback(() => {
    if (sessionIdRef.current) {
      gsm.current.endGameSession(sessionIdRef.current, false);
      sessionIdRef.current = null;
    }
    onExit?.();
  }, [onExit]);

  // Trigger AI on initial load if AI goes first
  useEffect(() => {
    if (view === 'playing' && visibleState) {
      scheduleAITurnIfNeeded();
    }
  }, [view]);

  // =========================================================================
  // Render
  // =========================================================================

  if (view === 'mode-select') {
    const unoConfig: GameModeConfig = {
      gameName: 'UNO',
      gameIcon: '🎴',
      subtitle: 'Classic card game',
      availableModes: ['ai', 'local', 'online'],
    };

    const handleModeSelection = (selection: GameModeSelection) => {
      if (selection.mode === 'ai') {
        handleStartGame({
          mode: 'singleplayer',
          aiDifficulty: (selection.difficulty as any) || 'medium',
          aiOpponentCount: 1,
        });
      } else if (selection.mode === 'local') {
        handleStartGame({
          mode: 'local-multiplayer',
          aiOpponentCount: 0,
        });
      } else if (selection.mode === 'online') {
        handleStartGame({ mode: 'multiplayer-realtime' });
      }
    };

    return (
      <div className="uno-game">
        <UniversalGameModeSelector
          config={unoConfig}
          onStart={handleModeSelection}
          isAuthenticated={isAuthenticated}
          guestGamesRemaining={isAuthenticated ? undefined : 3}
        />
      </div>
    );
  }

  if (!visibleState) return null;

  const isHumanTurn = visibleState.players[visibleState.currentPlayerIndex]?.id === humanPlayerId.current;

  const gameOverConfig = view === 'game-over' ? {
    show: true,
    result: (engineRef.current.getWinner() === humanPlayerId.current ? 'win' : 'loss') as 'win' | 'loss',
    title: '🎉 Game Over!',
    subtitle: engineRef.current.getWinner() === humanPlayerId.current
      ? '🏆 You Won!'
      : `${engineRef.current.getGameState().players.find(p => p.id === engineRef.current.getWinner())?.name || 'AI'} Wins!`,
    icon: engineRef.current.getWinner() === humanPlayerId.current ? '🏆' : '😔',
    onPlayAgain: handleNewGame,
    onExit: handleExit,
  } : undefined;

  const layoutConfig: UniversalGameLayoutConfig = {
    gameName: 'UNO',
    board: (
      <div className="uno-game__content">
        <UnoBoard
          gameState={visibleState}
          currentPlayerId={humanPlayerId.current}
          onPlayCard={handlePlayCard}
          onDrawCard={handleDrawCard}
          onCallUno={handleCallUno}
          isCurrentPlayerTurn={isHumanTurn}
          playerHand={playerHand}
        />

        {/* Drawn card prompt */}
        {drawnCard && isHumanTurn && (
          <div className="uno-game__drawn-prompt" role="status">
            <p>You drew a card. Play it or pass?</p>
            <div className="uno-game__drawn-actions">
              <button
                className="uno-game__btn uno-game__btn--primary"
                onClick={() => handlePlayCard(drawnCard)}
              >
                Play Card
              </button>
              <button
                className="uno-game__btn uno-game__btn--secondary"
                onClick={() => {
                  engineRef.current.passTurn(humanPlayerId.current);
                  setDrawnCard(null);
                  refreshState();
                  setTimeout(() => scheduleAITurnIfNeeded(), 500);
                }}
              >
                Pass
              </button>
            </div>
          </div>
        )}
      </div>
    ),
    leftPanels: [
      {
        id: 'info',
        title: 'Game Info',
        icon: '📋',
        content: (
          <div style={{ padding: '8px' }}>
            <div>Round: {visibleState.roundNumber}</div>
            <div>Cards in hand: {playerHand.length}</div>
            {visibleState.players.map(p => (
              <div key={p.id} style={{ marginTop: '4px' }}>
                {p.name}: {p.cardCount} cards
              </div>
            ))}
          </div>
        ),
      },
    ],
    statusBar: {
      turnIndicator: (
        <span>
          {view === 'game-over'
            ? 'Game Over'
            : isHumanTurn
              ? '🎯 Your Turn'
              : `⏳ Waiting for ${visibleState.players[visibleState.currentPlayerIndex]?.name}...`}
        </span>
      ),
      gameStatus: view === 'game-over' ? 'Game Over' : `Round ${visibleState.roundNumber}`,
      gameStatusType: view === 'game-over' ? 'ended' : 'active',
      aiThinking: !isHumanTurn && view !== 'game-over',
    },
    actions: [
      { id: 'new-game', label: 'New Game', icon: '🔄', onClick: handleNewGame, variant: 'primary' },
      { id: 'exit', label: 'Exit', icon: '🚪', onClick: handleExit, variant: 'secondary' },
    ],
    gameOver: gameOverConfig,
  };

  return (
    <div className="uno-game">
      <UniversalGameLayout config={layoutConfig} />
    </div>
  );
};

// ============================================================================
// Helper
// ============================================================================

function getCardDescription(card: UnoCard, chosenColor?: UnoColor): string {
  const colorName = card.color ? card.color.charAt(0).toUpperCase() + card.color.slice(1) : '';
  switch (card.type) {
    case 'number': return `${colorName} ${card.value}`;
    case 'skip': return `${colorName} Skip`;
    case 'reverse': return `${colorName} Reverse`;
    case 'draw-two': return `${colorName} Draw Two`;
    case 'wild': return `Wild (${chosenColor || 'any'})`;
    case 'wild-draw-four': return `Wild Draw Four (${chosenColor || 'any'})`;
    default: return 'card';
  }
}

// ============================================================================
// Wrapped Export
// ============================================================================

const UnoGame: React.FC<UnoGameProps> = (props) => (
  <ErrorBoundary componentName="UNO">
    <UnoGameInner {...props} />
  </ErrorBoundary>
);

export { UnoGame };
export default UnoGame;
